import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { defaultStorageItemValues, IMovement, TModuleOptions, TStorageItemRS, IMovementRS, IMovementElementItem } from '../models/storageModel';

export type TStorageView = "TABLE"|"LIST"
export type TStorage = {
  registerList: TStorageItemRS[]
  pendingListEntries: IMovement[],
  pendingListExits: IMovement[],
  itemListEntries: IMovementElementItem[],
  itemListExits: IMovementElementItem[],
  currentStorageItem: TStorageItemRS,
  currentMovementItem: IMovementElementItem,
  currentPending: IMovementRS,
  currentMovement: IMovementRS,
  movementList: IMovement[],
  displayOptions: TModuleOptions[],
  categoryOptions: TModuleOptions[],
  taxesOptions: TModuleOptions[],
  paymentOptions: TModuleOptions[],
  itemTypes: TModuleOptions[],
  umOptions: TModuleOptions[],
  movementTypesEntry: TModuleOptions[],
  movementTypesExit: TModuleOptions[],
  showAddItem: boolean,
  showCreateMovement: boolean,
  showInventory: boolean,
  storageViewType: TStorageView,
  showProgress: boolean,
  currentMovementType?: string | null,
  datePickerFilter?: string[] | null
}

const defaultMovementValues:IMovementRS = {
  approved_by_user_id: 0,
  approved_by_user_name: "",
  approved_by_user_document: "",
  delivered_by_user_id: 0,
  delivered_by_user_name: "",
  delivered_by_user_document: "",
  received_by_user_id: 0,
  received_by_user_name: "",
  received_by_user_document: "",
  created_by_user_id: 0,
  created_by_user_name: "",
  created_by_user_document: "",
  id: -1,
  document_type: "PURCHASE_ORDER",
  company_id: 0,
  document_number:"",
  status: "PENDING",
  contact_id: 0,
  contact_name:"",
  contact_document: "",
  notes: "",
  reference_document:"",
  invoice_type: "SIMPLE_RECEIPT",
  total_amount:0,
  storehouse_from_id: 0,
  storehouse_to_id: 0,
  updated_at:"",
  created_at: "",
  item_list: [],
  taxes_list: [],
  total_tax_amount:0,
  total_discount_amount:0,
  total_amount_net: 0,
  total_amount_full: 0,
  payment_term: "CASH",
  credit_days: 0,
  credit_due_date: "",
  cash_return_amount: 0,
  payments: [],
  items:0,
  type:"",
  uid:""
}
const initialState:TStorage = {
  registerList: [],
  pendingListEntries: [],
  pendingListExits: [],
  itemListEntries: [],
  itemListExits: [],
  movementTypesEntry: [],
  movementTypesExit: [],
  currentStorageItem: defaultStorageItemValues,
  currentPending: defaultMovementValues,
  currentMovement: defaultMovementValues,
  movementList: [],
  displayOptions: [],
  categoryOptions: [],
  taxesOptions: [],
  itemTypes: [],
  umOptions: [],
  paymentOptions: [],
  showAddItem: false,
  showCreateMovement: false,
  currentMovementItem: {
    key: "",
    register: 0,
    document_id: 0,
    quantity_available: 0,
    unit_price: 0,
    quantity: 0,
    id: 0,
    uid: '',
    total_amount: 0,
    total_tax_amount: 0,
    taxes_list: [],
    discount_rate: 0,
    discount_amount: 0,
    total_amount_full: 0,
    total_amount_net: 0,
    display: '',
    register_type: '',
    inventory:[],
    module_options: [],
    name: '',
    custom_id: "",
    item_name: "",
    item_code: "",
    item_description: ""
  },
  showInventory: false,
  storageViewType: "TABLE",
  showProgress: false,
  currentMovementType: null,
  datePickerFilter: null
}
interface RegisterContextType {
  register: TStorage;
  updateRegister: (newConfig: Partial<TStorage>) => void;
}
interface RegisterProviderProps {
  children: ReactNode;
}
const RegisterContext = createContext<RegisterContextType>({register:initialState,updateRegister:()=>{}});

export const RegisterProvider:React.FC<RegisterProviderProps> = ({ children }) => {
  const [register, setRegister] = useState(initialState);
  const updateRegister = useCallback((newConfig:Partial<TStorage>) => {
    setRegister((prevConfig) => ({ ...prevConfig, ...newConfig }));
  }, []);

  const contextValue = useMemo(() => ({
    register,
    updateRegister,
  }), [register, updateRegister]);

  return (
    <RegisterContext.Provider value={contextValue}>
      {children}
    </RegisterContext.Provider>
  );
};

export const useRegister = () => {
  return useContext(RegisterContext);
};